import { Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { ScrollTag } from 'utoniq-ui'

import { EventCollectionPickupTimeFragment } from '@/clients/utoniq-core/schema'
import { tLabel, useForm, UseFormProps, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

type FilterFormProps = {
  pickupDate: Date
  eventCollection: EventCollectionPickupTimeFragment
  onSubmit: (val: Date) => Promise<void>
}

type FilterFormValue = {
  pickupDate: string
  pickupTime?: string
}

const schema: UseFormProps['schema'] = o => ({
  pickupDate: o
    .string()
    .label(tLabel('storeItemPurchaseProceed.pickupDate'))
    .required(),
  pickupTime: o
    .string()
    .label(tLabel('storeItemPurchaseProceed.pickupTime'))
    .optional(),
})

export const FilterForm: Organism<FilterFormProps> = ({
  eventCollection,
  pickupDate,
  onSubmit,
}) => {
  const { t } = useTranslation('consoleQuantityManagement')

  const { watch, reset, setValue } = useForm<FilterFormValue>({
    schema,
    defaultValues,
  })

  const watchPickupDate = watch('pickupDate')
  const watchPickupTime = watch('pickupTime')

  useEffect(() => {
    const pickupTime = eventCollection?.pickupTime
    if (!pickupTime) {
      return
    }
    if (!pickupDate) {
      return
    }
    if (
      !pickupTime.some(
        value =>
          DateTime.formatDate(pickupDate, 'YYYY/MM/DD') ===
          DateTime.formatDate(value.options.time, 'YYYY/MM/DD')
      )
    ) {
      reset({
        pickupDate: String(DateTime.formatDate(pickupDate, 'YYYY/MM/DD')),
        pickupTime: '',
      })
    }
    reset({
      pickupDate: String(DateTime.formatDate(pickupDate, 'YYYY/MM/DD')),
      pickupTime: '00:00',
    })
  }, [])

  // 日付ごとに時間をマッピングしたもの
  const actualPickupTime = Object.entries(
    eventCollection.pickupTime
      ?.map(({ options }) => ({
        date: String(DateTime.formatDate(options.time, 'MM/DD')),
        time: options.time,
      }))
      .reduce((acc, { date, time }) => {
        if (!acc[date]) acc[date] = []
        acc[date].push({ time })
        return acc
      }, {} as any)
  ).map(([date, times]) => ({
    date,
    times,
  })) as {
    date: string
    times: { time: string }[]
  }[]

  const pickupTimes = useMemo(
    () => [
      { label: t('all', 'すべて'), value: '00:00' },
      ...(actualPickupTime
        .find(
          ({ date }) => date === DateTime.formatDate(watchPickupDate, 'MM/DD')
        )
        ?.times?.map(({ time }) => ({
          value: String(DateTime.formatDate(time, 'HH:mm')),
          label: `${String(DateTime.formatDate(time, 'HH:mm'))} \n〜 ${String(
            DateTime.formatDate(
              dayjs(time)
                .add(eventCollection?.validMinutes ?? 0, 'minute')
                .valueOf(),
              'HH:mm'
            )
          )}`,
        })) ?? []),
    ],
    [actualPickupTime, eventCollection?.validMinutes, t, watchPickupDate]
  )

  return (
    <OrganismFrame sx={{ mx: 5 }}>
      <Typography variant="subtitle1">{t('receiptDate', '受取日')}</Typography>
      <ScrollTag
        value={watchPickupDate}
        tags={actualPickupTime.map(({ date, times }) => ({
          value: String(DateTime.formatDate(times[0].time, 'YYYY/MM/DD')),
          label: date,
        }))}
        onClick={val => {
          setValue('pickupDate', val)
          setValue('pickupTime', '00:00')
          onSubmit(dayjs(`${val} 00:00:00`, 'YYYY/MM/DD HH:mm:ss').toDate())
        }}
      />
      <ScrollTag
        value={watchPickupTime}
        tags={pickupTimes}
        onClick={val => {
          setValue('pickupTime', val)
          onSubmit(
            dayjs(
              `${watchPickupDate} ${val}:00`,
              'YYYY/MM/DD HH:mm:ss'
            ).toDate()
          )
        }}
      />
    </OrganismFrame>
  )
}

const defaultValues: FilterFormValue = {
  pickupDate: '',
  pickupTime: '',
}
